/**
 * <nearest-retailer> Web Component
 *
 * Fetches the geographically closest retailer for the current product's
 * collections from the retailer-worker API, then renders a compact link.
 *
 * Attributes:
 *   data-collections  Comma-separated collection handles (from Liquid)
 *   data-api-url      Base URL of the retailer-worker (Cloudflare Worker)
 *
 * Renders:
 *   "Near you: <a>Retailer Name (City, ST ZIP)</a>"
 *   Falls back to: "<a>Find retailers</a>"  on error / no result
 */

/** @type {Map<string, Promise<{name:string, city:string, state:string, postcode:string, slug:string}|null>>} */
const retailerCache = new Map();

/**
 * Fetch the closest retailer from the worker API.
 * @param {string} apiUrl
 * @param {string} collections
 * @returns {Promise<{name:string, city:string, state:string, postcode:string, slug:string}|null>}
 */
function fetchClosest(apiUrl, collections) {
  const cacheKey = `${apiUrl}|${collections}`;
  if (retailerCache.has(cacheKey)) return /** @type {Promise<any>} */ (retailerCache.get(cacheKey));

  const url = `${apiUrl}/closest${collections ? `?collections=${encodeURIComponent(collections)}` : ''}`;

  const promise = fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error(`Retailer API ${res.status}`);
      return res.json();
    })
    .then((json) => {
      const loc = json?.closest_location;
      if (!loc) return null;
      return {
        name: loc.name || '',
        city: loc.city || '',
        state: loc.state || '',
        postcode: loc.postcode || '',
        slug: loc.slug || '',
      };
    })
    .catch((err) => {
      console.warn('[nearest-retailer] API error, showing fallback:', err);
      retailerCache.delete(cacheKey);
      return null;
    });

  retailerCache.set(cacheKey, promise);
  return promise;
}

class NearestRetailer extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  async render() {
    const apiUrl = this.dataset.apiUrl;
    const collections = this.dataset.collections || '';
    const fallbackUrl = '/pages/retailers';

    if (!apiUrl) {
      this.renderFallback(fallbackUrl);
      return;
    }

    const location = await fetchClosest(apiUrl, collections);

    if (location && location.name) {
      const locationParts = [location.city, location.state, location.postcode]
        .filter(Boolean)
        .join(location.postcode && !location.state ? ' ' : ', ')
        .replace(/, (\d)/, ' $1'); // "City, ST ZIP" format

      // Build a city + state label like "Miami, FL 33134"
      const label = locationParts ? `${location.name} (${locationParts})` : location.name;

      const href = location.slug
        ? `${fallbackUrl}?location=${encodeURIComponent(location.slug)}`
        : fallbackUrl;

      this.innerHTML = /* html */ `
        <span class="nearest-retailer__label">Near you: </span><a class="nearest-retailer__link" href="${href}">${label}</a>
      `;
    } else {
      this.renderFallback(fallbackUrl);
    }
  }

  /**
   * @param {string} url
   */
  renderFallback(url) {
    this.innerHTML = /* html */ `
      <a class="nearest-retailer__link" href="${url}">Find retailers</a>
    `;
  }
}

if (!customElements.get('nearest-retailer')) {
  customElements.define('nearest-retailer', NearestRetailer);
}
